
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import { debounce, difference } from 'lodash';
import { DateTime } from 'luxon';

import CurrencyAmountField from '../../../../../framework/fields/CurrencyAmountField.vue';
import SelectField from '../../../../../framework/fields/SelectField.vue';
import { sharedState } from '../../../../../framework/state';
import {
    deleteResource,
    getResource,
    getResourcePagingNew,
    putResource,
} from '../../../../../framework/client/resource';
import {
    getEnumerationOptions,
    getOptionLabel,
    getResourceOptions,
    OptionValue,
} from '../../../../../framework/service/options';
import TextField from '../../../../../framework/fields/TextField.vue';
import {
    dateToDbDateString,
    lowerCamelToUpperCamel,
    underscoreToCamel,
} from '../../../../../../common/framework/util/convert';
import { SortOrder } from '../../../../../../common/framework/model/SortOrder';

import AutoCompleteMultipleField from '../../../../fields/AutoCompleteMultipleField.vue';

import { getCustomerOptions, getVendorOptions } from '../../../../service/application_options_service';
import { VehicleContract } from '../../../../../../common/application/model/VehicleContract';
import { VehicleState, CustomerVehicleState } from '../../../../../../common/application/model/enums/VehicleState';
import { getTagTypeFromVehicleState } from '../../../../service/tag_type_service';
import { Customer } from '../../../../../../common/application/model/Customer';
import { VendorContract } from '../../../../../../common/application/model/VendorContract';
import { PageSize } from '../../../../../../common/application/model/enums/PageSize';
import {
    calculateTotalVatFromMonthlyRows,
    formatCurrency,
    ignoredVehicleContractColumns,
    initialVehicleContractColumns,
    searchAbleVehicleContractColumns,
    sortAlphabetically,
    mapMonthlyCostsToVehicleContract,
} from '../../../../service/helper_service';
import { updateCachedResourceRow } from '../../../../service/query_cache_service';
import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import {
    constructMonthsFromNowString,
    monthlyVehicleContractCostColumns,
} from '../../../../../../common/application/service/query_helper_service';
import { successToast, errorToast } from '../../../../service/toast_service';
import {
    exportVehicleContracts,
    getVehicleContracts,
    updateMonthlyRowsForVehicleContract,
} from '../../../../client/vehicle_contract_client';
import { ApplicationUserRole } from '../../../../../../common/application/enumeration/ApplicationUserRole';
import { VendorContractMap } from '../../../../../../common/application/model/VendorContractMap';
import { MonthlyCostRowMap } from '../../../../../../common/application/model/MonthlyCostRowMap';
import { VehicleContractNumberMap } from '../../../../../../common/application/model/VehicleContractNumberMap';
import { SmartFleetContractType } from '../../../../../../common/application/model/enums/SmartFleetContractType';
import { FinancingContractType } from '../../../../../../common/application/model/enums/FinancingContractType';
import { VehicleElectricalClass } from '../../../../../../common/application/model/enums/VehicleElectricalClass';
import { VehicleEnginePowerType } from '../../../../../../common/application/model/enums/VehicleEnginePowerType';

import MoveVehicleContractWizard from './MoveVehicleContractWizard.vue';
import CopyVehicleContractWizard from './CopyVehicleContractWizard.vue';
import EndVehicleContractWizard from './EndVehicleContractWizard.vue';
import EndAndMoveVehicleContractWizard from './EndAndMoveVehicleContractWizard.vue';
import ParameterService from '../../../../service/parameter_service';
import { getVehicleContractExportFile } from '../../../../client/file_meta_client';
import MonthField from '../../../../../framework/fields/MonthField.vue';
import { VendorType } from '../../../../../../common/application/model/enums/VendorType';
import { BudgetType } from '../../../../../../common/application/model/enums/BudgetType';

@Component({
    components: {
        MoveVehicleContractWizard,
        CopyVehicleContractWizard,
        EndVehicleContractWizard,
        EndAndMoveVehicleContractWizard,
        SelectField,
        TextField,
        CurrencyAmountField,
        AutoCompleteMultipleField,
        MonthField,
    },
})
export default class VehicleContracts extends Vue {
    readonly resourceType = ApplicationResource.VEHICLE_CONTRACT;
    parameterService = new ParameterService(this.resourceType);

    @Prop(String) readonly mode!: string;
    @Prop(String) readonly customerId!: string;

    shared = sharedState;
    local = {
        customer: undefined as Customer | undefined,
        rows: new Array<VehicleContract>(),
        total: 0,
        loading: false,
        selected: undefined as VehicleContract | undefined,
        selectedCustomerSetByUser: true,
        vehicleStateOptions: [] as OptionValue[],
        smartFleetContractTypeOptions: [] as OptionValue[],
        financingContractTypeOptions: [] as OptionValue[],
        vehicleEnginePowerTypeOptions: [] as OptionValue[],
        vehicleElectricalClassOptions: [] as OptionValue[],
        customerOptions: [] as OptionValue[],
        vendorOptions: [] as OptionValue[],
        vehicleMakeOptions: [] as OptionValue[],
        pageSizeOptions: [] as number[],
        initialColumns: initialVehicleContractColumns,
        ignoredColumns: ignoredVehicleContractColumns,
        monthlyCostColumns: monthlyVehicleContractCostColumns,
        columns: [] as { property: string; localizedValue: string }[],
        selectedColumns: this.getSelectedColumns(),
        searchAbleColumns: searchAbleVehicleContractColumns,
        columnListMaxHeight: 300,
        columnListIsScrollable: true,
        vendorContracts: {} as VendorContractMap,
        monthlyCostRows: {} as MonthlyCostRowMap,
        parameters: this.parameterService.get(),
        showWithVat: false,
        showSearchInfo: false,
        smartFleetContractEnd: undefined as number | undefined,
        hover: '',
        editingRow: {} as VehicleContract,
        vehicleContractNumbers: {} as VehicleContractNumberMap,
        selectedCustomers: [] as string[],
        isMoveWizardActive: false,
        isCopyWizardActive: false,
        isEndWizardActive: false,
        isEndAndMoveWizardActive: false,
        expandedView: this.getExpandedView(),
        search: '' as string | undefined,
        smartFleetContractStart: undefined as string | undefined,
    };

    // InvoiceRowTypes in VehicleContract 33001, 33000, 14055, 14056, 14057
    // to ignore: 14052
    // can be any: 14054

    async mounted() {
        if (this.$route.params && this.$route.params.customerId) {
            this.local.parameters.customerId = this.$route.params.customerId.split(',');
            this.parameterService.set(this.local.parameters);
        }

        this.local.parameters = this.filterIfCustomer(this.local.parameters);
        this.setSearchString();
        await this.loadAsyncData();
        this.setupColumns();
        this.local.customerOptions = await getCustomerOptions();
        this.local.vendorOptions = await getVendorOptions();
        this.local.vehicleMakeOptions = await getResourceOptions(ApplicationResource.VEHICLE_MAKE, 'name');
        this.local.vehicleStateOptions = getEnumerationOptions(
            'VehicleState',
            Object.keys(this.isAdmin ? VehicleState : CustomerVehicleState),
            this,
        );
        this.local.smartFleetContractTypeOptions = getEnumerationOptions(
            'SmartFleetContractType',
            Object.keys(SmartFleetContractType),
            this,
        );
        this.local.financingContractTypeOptions = getEnumerationOptions(
            'FinancingContractType',
            Object.keys(FinancingContractType),
            this,
        );
        this.local.vehicleEnginePowerTypeOptions = getEnumerationOptions(
            'VehicleEnginePowerType',
            Object.keys(VehicleEnginePowerType),
            this,
        );
        this.local.vehicleElectricalClassOptions = getEnumerationOptions(
            'VehicleElectricalClass',
            Object.keys(VehicleElectricalClass),
            this,
        );
        this.local.pageSizeOptions = Object.values(PageSize).filter((o) => typeof (o as any) === 'number') as number[];
        this.local.smartFleetContractEnd =
            DateTime.fromJSDate(new Date(this.local.parameters.smartFleetContractEnd)).minus({
                months: DateTime.now().month - 1,
            }).month - 1 || undefined;
    }

    async loadAsyncData() {
        this.local.loading = true;

        const backendParameters = this.parameterService.getBackendParameters();
        const customPart = backendParameters.get('custom');
        backendParameters.delete('custom');

        this.local.total = (await getResourcePagingNew(this.resourceType, backendParameters, customPart)).rowCount;
        const contractResult = await getVehicleContracts(backendParameters, customPart);

        this.local.rows = contractResult.vehicleContracts;
        this.local.monthlyCostRows = contractResult.monthlyCostRowMap;
        this.local.vendorContracts = contractResult.vendorContractMap;
        this.local.vehicleContractNumbers = contractResult.vehicleContractNumbers;

        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }

        if (this.customerId) {
            this.local.customer = await getResource<Customer>(ApplicationResource.CUSTOMER, this.customerId);
        }

        this.local.loading = false;
    }

    addOrder() {
        this.$router.push(`/order/add`);
    }

    add() {
        this.$router.push(`/${ApplicationResource.VEHICLE_CONTRACT}/add`);
    }

    edit(id?: string) {
        if (id) {
            this.$router.push(`/${ApplicationResource.VEHICLE_CONTRACT}/${id}/edit`);
        } else if (this.local.selected) {
            this.$router.push(`/${ApplicationResource.VEHICLE_CONTRACT}/${this.local.selected.id}/edit`);
        }
    }

    view() {
        if (this.local.selected) {
            this.$router.push(`/${ApplicationResource.VEHICLE_CONTRACT}/${this.local.selected.id}`);
        }
    }

    confirmDelete() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDelete').toString(),
            message: this.$t('message.confirmDelete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                if (this.local.selected) {
                    try {
                        await deleteResource(this.resourceType, this.local.selected.id);
                        successToast(this, 'message.resourceDeleted');
                        this.$router.go(0);
                    } catch (e) {
                        errorToast(this);
                    }
                }
            },
        });
    }

    moveContract() {
        this.local.isMoveWizardActive = true;
    }

    copyContract() {
        this.local.isCopyWizardActive = true;
    }

    endContract() {
        this.local.isEndWizardActive = true;
    }

    endContractAndMoveToNewCustomer() {
        this.local.isEndAndMoveWizardActive = true;
    }

    canEndContract(selected: VehicleContract | undefined): boolean {
        if (selected) {
            const now = new Date();
            const twoMonthsAhead = new Date(now.setMonth(now.getMonth() + 2)).toISOString();

            const vendorContracts = [];

            for (const contract of Object.values(this.local.vendorContracts[selected.id])) {
                if (!contract.id) {
                    continue;
                }

                if (
                    contract.budgetType === BudgetType.BUDGETED &&
                    contract.endDate &&
                    new Date(contract.endDate) > new Date(twoMonthsAhead)
                ) {
                    vendorContracts.push(contract);
                }
            }

            return vendorContracts.length > 0;
        } else {
            return false;
        }
    }

    async contractMoved() {
        if (this.local.selected) {
            this.local.isMoveWizardActive = false;
            this.local.selected = undefined;
            await this.loadAsyncData();
            successToast(this, 'message.vehicleContractMoved');
        }
    }

    async contractEnded() {
        if (this.local.selected) {
            this.local.isEndWizardActive = false;
            this.local.selected = undefined;
            await this.loadAsyncData();
            successToast(this, 'message.endContract.vehicleContractEnded');
        }
    }

    async contractMovedAndEnded() {
        console.log('emitted');
        if (this.local.selected) {
            this.local.isEndAndMoveWizardActive = false;
            this.local.selected = undefined;
            await this.loadAsyncData();
            successToast(this, 'message.vehicleContractEndedAndMoved');
        }
    }

    async contractCopied() {
        this.hideCopyWizardModal();
        await this.loadAsyncData();
        this.local.selected = undefined;
        successToast(this, 'message.vehicleContractCopied');
    }

    hideMoveWizardModal() {
        this.local.isMoveWizardActive = false;
    }
    hideCopyWizardModal() {
        this.local.isCopyWizardActive = false;
    }
    hideEndWizardModal() {
        this.local.isEndWizardActive = false;
    }
    hideEndAndMoveWizardModal() {
        this.local.isEndAndMoveWizardActive = false;
    }

    async exportToExcel(): Promise<void> {
        const backendParameters = this.parameterService.getBackendParameters();
        const customPart = backendParameters.get('custom');
        backendParameters.delete('custom');

        const response = await exportVehicleContracts(backendParameters, customPart);
        const result = await getVehicleContractExportFile(response.fileName);
        const aElement = document.createElement('a');
        aElement.setAttribute('download', response.fileName);
        const href = URL.createObjectURL(result);
        aElement.href = href;
        aElement.setAttribute('target', '_blank');
        aElement.click();
        URL.revokeObjectURL(href);
    }

    @Watch('local.parameters.customerId')
    async selectedCustomerIdsChange() {
        if (this.local.loading) {
            return;
        }

        await this.resetPagingAndLoadData();
    }

    @Watch('local.search')
    searchChange(searchString: string) {
        if (this.local.loading) {
            return;
        }

        this.deBounceSearch(searchString);
    }

    @Watch('local.parameters.pageSize')
    async pageSizeChange() {
        if (this.local.loading) {
            return;
        }

        await this.resetPagingAndLoadData();
    }

    @Watch('local.smartFleetContractEnd')
    async smartFleetContractEndChange(endingInMonths: string | undefined) {
        if (this.local.loading) {
            return;
        }

        const searchQuery = `smartFleetContractEnd<{smartFleetContractEnd} AND smartFleetContractEnd>{smartFleetContractEnd2}`;

        delete this.local.parameters.smartFleetContractEnd;
        delete this.local.parameters.smartFleetContractEnd2;

        if (this.local.parameters.custom && this.local.parameters.custom.startsWith(searchQuery)) {
            this.local.parameters.custom = this.local.parameters.custom.replace(`${searchQuery}`, '');
        } else if (this.local.parameters.custom) {
            this.local.parameters.custom = this.local.parameters.custom.replace(`AND ${searchQuery}`, '');
        }

        if (endingInMonths !== undefined) {
            const monthsFromNow = constructMonthsFromNowString(parseInt(endingInMonths));
            const monthsNow = constructMonthsFromNowString(0);
            this.local.parameters.smartFleetContractEnd = monthsFromNow;
            this.local.parameters.smartFleetContractEnd2 = monthsNow;

            if (this.local.parameters.custom && this.local.parameters.custom.length) {
                this.local.parameters.custom += ` AND ${searchQuery}`;
            } else {
                this.local.parameters.custom = `${searchQuery}`;
            }
        }

        await this.resetPagingAndLoadData();
    }

    @Watch('local.smartFleetContractStart')
    async smartFleetContractStartChange() {
        if (this.local.loading) {
            return;
        }

        const searchQuery = `smartFleetContractStart>={smartFleetContractStart}`;

        delete this.local.parameters.smartFleetContractStart;

        if (this.local.parameters.custom && this.local.parameters.custom.startsWith(searchQuery)) {
            this.local.parameters.custom = this.local.parameters.custom.replace(`${searchQuery}`, '');
        } else if (this.local.parameters.custom) {
            this.local.parameters.custom = this.local.parameters.custom.replace(`AND ${searchQuery}`, '');
        }

        if (this.local.smartFleetContractStart) {
            this.local.parameters.smartFleetContractStart = this.local.smartFleetContractStart;

            if (this.local.parameters.custom && this.local.parameters.custom.length) {
                this.local.parameters.custom += ` AND ${searchQuery}`;
            } else {
                this.local.parameters.custom = `${searchQuery}`;
            }
        }

        await this.resetPagingAndLoadData();
    }

    @Watch('local.selectedColumns')
    selectedColumnsChange() {
        if (this.local.loading) {
            return;
        }

        this.saveSelectedColumns();
    }

    async dropdownClosed(
        resourceIds: string[],
        idField: 'customerId' | 'id' | 'financingContractFinancingVendorId',
    ): Promise<void> {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = [...resourceIds];
        }

        await this.resetPagingAndLoadData();
    }

    async vehicleStatesSelected(resourceIds: VehicleState[], idField: 'vehicleState'): Promise<void> {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = resourceIds;
        }

        await this.resetPagingAndLoadData();
    }

    async onSortChange(field: string, order: SortOrder) {
        this.local.parameters.sortField = field;
        this.local.parameters.sortOrder = order;
        await this.resetPagingAndLoadData();
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    getCustomerName(id: string): string {
        return getOptionLabel(id, this.local.customerOptions);
    }

    getVehicleMakeLabel(id: string): string {
        return getOptionLabel(id, this.local.vehicleMakeOptions);
    }

    getVendorName(id: string): string {
        return getOptionLabel(id, this.local.vendorOptions);
    }

    dateToString(date: string | undefined): string {
        if (!date) {
            return '';
        }

        return dateToDbDateString(new Date(date));
    }

    getVehicleState(id: string): string {
        return getOptionLabel(id, this.local.vehicleStateOptions);
    }

    getSmartFleetContractType(id: string): string {
        return getOptionLabel(id, this.local.smartFleetContractTypeOptions);
    }

    getFinancingContractType(id: string): string {
        return getOptionLabel(id, this.local.financingContractTypeOptions);
    }

    getVehicleEnginePowerType(id: string): string {
        return getOptionLabel(id, this.local.vehicleEnginePowerTypeOptions);
    }

    getVehicleElectricalClass(id: string): string {
        return getOptionLabel(id, this.local.vehicleElectricalClassOptions);
    }

    getTagType(id: VehicleState): string {
        return getTagTypeFromVehicleState(id);
    }

    formatCurrencyValue(value: number | null | undefined) {
        return formatCurrency(value);
    }

    toCamel(str: string) {
        if (!str) {
            return 'Contracts';
        }
        return lowerCamelToUpperCamel(underscoreToCamel(str));
    }

    toggleView() {
        this.setExpandedView(!this.local.expandedView);
        this.local.expandedView = this.getExpandedView();
        this.clearSelectedColumns();
        this.setupColumns();
    }

    async resetFilter(): Promise<void> {
        this.local.parameters = this.parameterService.getDefault();
        this.local.smartFleetContractStart = undefined;
        this.local.smartFleetContractEnd = undefined;
        await this.resetPagingAndLoadData();
    }

    isColumnVisible(column: string) {
        return this.local.selectedColumns.indexOf(column) > -1;
    }

    onRowClick(row: VehicleContract) {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    editRow(row: VehicleContract): void {
        if (!this.isAdmin) {
            return;
        }

        this.local.editingRow = row;
    }

    async saveInlineEditRow() {
        const updatedVehicleContractValues = mapMonthlyCostsToVehicleContract(
            this.local.editingRow,
            this.local.vehicleContractNumbers,
        );

        this.local.loading = true;
        try {
            const updatedVehicleContract = await putResource<VehicleContract>(
                this.resourceType,
                this.local.editingRow.id,
                updatedVehicleContractValues,
            );

            successToast(this, 'message.vehicleContractSaved');
            updateCachedResourceRow<VehicleContract>(
                updatedVehicleContract,
                updatedVehicleContract.id,
                this.resourceType,
            );

            for (const contract of Object.values(this.local.vendorContracts[this.local.editingRow.id])) {
                if (!contract.id) {
                    continue;
                }

                const updatedVendorContract = await putResource<VendorContract>(
                    ApplicationResource.VENDOR_CONTRACT,
                    contract.id,
                    contract,
                );
                updateCachedResourceRow<VendorContract>(
                    updatedVendorContract,
                    this.local.editingRow.id,
                    ApplicationResource.VENDOR_CONTRACT,
                );
            }

            successToast(this, 'message.vendorContractSaved');
            await updateMonthlyRowsForVehicleContract(this.local.editingRow.customerId, this.local.editingRow.id);
            this.local.editingRow = {} as VehicleContract;
            await this.loadAsyncData();
        } catch (error) {
            errorToast(this);
        }

        this.local.loading = false;
    }

    cancel() {
        this.local.editingRow = {} as VehicleContract;
    }

    showInlineEdit(row: VehicleContract): boolean {
        return !(this.local.hover !== row.id || !this.isAdmin);
    }

    isFromVehicleContract(rowType: string, row: VehicleContract): boolean {
        return !!this.local.vehicleContractNumbers[row.id][rowType];
    }

    showOnlyMonthlyCostColumns(): void {
        this.clearSelectedColumns();
        this.local.selectedColumns = ['vehicleState', 'smartFleetContractNumber'];
        this.local.monthlyCostColumns.forEach((column) => this.local.selectedColumns.push(column));
    }

    calculateTotalVat(vehicleContractId: string, type: 'invoicedVat' | 'projectedVat'): number {
        return calculateTotalVatFromMonthlyRows(vehicleContractId, type, this.local.monthlyCostRows);
    }

    get pathHasCustomerId(): boolean {
        return this.$route.query.hasOwnProperty('customerId') || this.$route.path.includes('company');
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get isManager(): boolean {
        return this.shared.hasRole(ApplicationUserRole.MANAGER);
    }

    vehicleRegistrationNumberParameters(): Map<string, string> {
        const parameters = new Map<string, string>();

        if (this.local.parameters.customerId) {
            parameters.set('customerId', this.local.parameters.customerId);
        }

        if (this.local.parameters.vehicleState) {
            parameters.set('vehicleState', this.local.parameters.vehicleState);
        }

        return parameters;
    }

    vehicleVehicleStateParameters(): Map<string, any> {
        const parameters = new Map<string, any>();

        if (this.local.parameters.customerId && this.local.parameters.customerId.length > 0) {
            parameters.set('customerId', this.local.parameters.customerId);
        }

        return parameters;
    }

    financingVendorParameters(): Map<string, any> {
        const parameters = new Map<string, any>();

        parameters.set('vendorType', VendorType.FINANCING_COMPANY);

        return parameters;
    }

    editInspectionDates(): void {
        this.$router
            .push(`/${ApplicationResource.VEHICLE_CONTRACT}/inspection-dates`)
            .then()
            .catch((e) => console.error(e));
    }

    vehicleContractLabel(row: VehicleContract): string {
        return `${row.smartFleetContractNumber} / ${row.vehicleRegistrationNumber || '-'} /
                            ${this.getVehicleMakeLabel(row.vehicleMakeId) || '-'}  ${row.vehicleModel || '-'}`;
    }

    private setupColumns() {
        if (!this.local.rows.length) {
            return;
        }

        this.local.columns = [];

        this.addVehicleContractModelColumns();
        this.addMonthlyCostColumns();

        this.local.columns.sort(sortAlphabetically('localizedValue'));

        const initialColumnsSet =
            difference(this.local.selectedColumns, this.local.initialColumns.slice()).length === 0;

        if (this.local.expandedView && initialColumnsSet) {
            this.local.selectedColumns = [];
            const selectedColumns: string[] = [];

            this.local.columns.forEach((column) => {
                const shouldBeIgnored = this.local.ignoredColumns.findIndex(
                    (ignoredColumn) => ignoredColumn === column.property,
                );

                if (shouldBeIgnored === -1) {
                    selectedColumns.push(column.property);
                }
            });
            this.local.selectedColumns = selectedColumns;
        } else if (initialColumnsSet) {
            this.local.selectedColumns = this.local.initialColumns.slice();
        }
    }

    private addVehicleContractModelColumns() {
        for (const key of Object.keys(this.local.rows[0])) {
            if (this.local.ignoredColumns.indexOf(key) > -1) {
                continue;
            }

            this.local.columns.push({
                property: key,
                localizedValue: this.$t(`field.${key}`).toString(),
            });
        }
    }

    private addMonthlyCostColumns() {
        for (const column of this.local.monthlyCostColumns) {
            this.local.columns.push({
                property: column,
                localizedValue: this.$t(`enum.invoiceRowType.${column}`).toString(),
            });
        }
    }

    private filterIfCustomer(parameters: Record<string, any>) {
        if (this.isAdmin) {
            return parameters;
        }

        const customerOnly: VehicleState[] = [
            VehicleState.ACTIVE,
            VehicleState.INACTIVE,
            VehicleState.ORDER,
            VehicleState.REALIZATION,
        ];

        this.local.parameters.vehicleState = parameters.vehicleState.filter((eachState: VehicleState) =>
            customerOnly.includes(eachState),
        );

        this.parameterService.set(this.local.parameters);
        return { ...this.local.parameters };
    }

    private deBounceSearch = debounce(async (searchString: string) => {
        await this.setSearch(searchString);
    }, 1200);

    private async setSearch(searchString: string | undefined) {
        if (searchString === '') {
            searchString = undefined;
        }
        const searchQuery = `(vehicleRegistrationNumber ILIKE {vehicleRegistrationNumber} OR vehicleModel ILIKE {vehicleModel} OR smartFleetInvoiceExtraReference ILIKE {smartFleetInvoiceExtraReference})`;

        if (this.local.parameters.custom && this.local.parameters.custom.startsWith(searchQuery)) {
            this.local.parameters.custom = this.local.parameters.custom.replace(searchQuery, '');
        } else if (this.local.parameters.custom) {
            this.local.parameters.custom = this.local.parameters.custom.replace(`AND ${searchQuery}`, '');
        }

        delete this.local.parameters.vehicleRegistrationNumber;
        delete this.local.parameters.vehicleModel;
        delete this.local.parameters.smartFleetInvoiceExtraReference;

        if (searchString) {
            this.local.parameters.vehicleRegistrationNumber = `%${searchString}%`;
            this.local.parameters.vehicleModel = `%${searchString}%`;
            this.local.parameters.smartFleetInvoiceExtraReference = `%${searchString}%`;

            if (this.local.parameters.custom && this.local.parameters.custom.length) {
                this.local.parameters.custom += ` AND ${searchQuery}`;
            } else {
                this.local.parameters.custom = `${searchQuery}`;
            }
        }

        await this.resetPagingAndLoadData();
    }

    private saveSelectedColumns() {
        localStorage.setItem('vehicleContractColumns', JSON.stringify(this.local.selectedColumns));
    }

    protected getSelectedColumns(): string[] {
        return JSON.parse(localStorage.getItem('vehicleContractColumns') || '[]') || [];
    }

    protected clearSelectedColumns() {
        this.local.selectedColumns = [];
        localStorage.removeItem('vehicleContractColumns');
    }

    private getExpandedView(): boolean {
        return localStorage.getItem(`${this.resourceType}_expanded_view`) === 'true' || false;
    }

    private setExpandedView(value: boolean) {
        localStorage.setItem(`${this.resourceType}_expanded_view`, value.toString());
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    private setSearchString(): void {
        if (this.local.parameters.custom && this.local.parameters.vehicleModel) {
            this.local.search = this.local.parameters.vehicleModel.toString().replaceAll('%', '');
        }
    }
}
